import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  AspectRatio, Grid, Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup, Portal,
  Skeleton,
  Stack,
  Text
} from "@chakra-ui/react";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Collection } from "../../../components/Collection";
import { colors } from "../../../core/constants/colors";
import { I18N_NAMESPACE } from "../../../core/constants/i18n";
import { ICollection } from "../../../core/models/collection";

interface Props {
  collections: ICollection[];
  isLoading: boolean;
  dateOptions: { label: string; value: string }[];
  selectedDateOption: string;
  onChangeDateOption: (value: string) => void;
}

export const TopCollections = React.memo((props: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation([I18N_NAMESPACE.DASHBOARD]);
  const selectionDateOption = useMemo(
    () => props.dateOptions.find((o) => o.value === props.selectedDateOption),
    [props.dateOptions, props.selectedDateOption]
  );

  const onClickCollection = useCallback((collection: ICollection) => {
    navigate(`/collections/${collection.id}`);
  }, [navigate]);

  return (
    <Stack spacing={5}>
      <Text fontSize={"2xl"} fontWeight={700}>
        {t("Top collections over last")}{" "}
        <Menu>
          <MenuButton color={colors.primary}>
            <Text as={"span"} fontWeight={700}>
              {selectionDateOption?.label}
            </Text>{" "}
            <ChevronDownIcon />
          </MenuButton>
          <Portal>
            <MenuList minWidth="240px">
              <MenuOptionGroup
                onChange={(value) => typeof value === "string" && props.onChangeDateOption(value)}
                value={props.selectedDateOption}
                type="radio"
              >
                {props.dateOptions.map((o) => (
                  <MenuItemOption key={o.value} value={o.value}>
                    {o.label}
                  </MenuItemOption>
                ))}
              </MenuOptionGroup>
            </MenuList>
          </Portal>
        </Menu>
      </Text>
      <Grid
        templateColumns={{
          base: "repeat(2, 1fr)",
          md: "repeat(3, 1fr)",
          lg: "repeat(4, 1fr)",
          xl: "repeat(5, 1fr)",
          "2xl": "repeat(6, 1fr)",
        }}
        gap={6}>
        {props.isLoading
          ? [0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
              <AspectRatio ratio={1 / 1} borderRadius="lg" key={`collection-skeleton-${i}`}>
                <Skeleton h={"full"} w={"full"} borderRadius="lg"></Skeleton>
              </AspectRatio>
            ))
          : props.collections.map((collection) => (
              <Collection collection={collection} key={collection.id} onClick={onClickCollection} />
            ))}
      </Grid>
    </Stack>
  );
});
